import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { motion } from 'motion/react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import PageBreadcrumb from 'src/components/PageBreadcrumb';
import useNavigate from '@fuse/hooks/useNavigate';
import { useCreateUserMutation, useDeleteUserMutation, useUpdateUserMutation } from '@/store/services/users';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import ConfirmationDialog from "@/pages/apps/users/Dialog/ConfirmationDialog";

import { useState } from 'react';
import { APIErrorResponse, isFetchBaseQueryError } from '@/utils/types';

function UserHeader() {
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const routeParams = useParams<{ userId: string }>();
	const { userId } = routeParams;

	const [createUser, { isLoading: isAdding }] = useCreateUserMutation();
	const [saveUser, { isLoading: isSaving }] = useUpdateUserMutation();
	const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

	const methods = useFormContext();
	const { formState, watch, getValues, handleSubmit } = methods;
	const { isValid, dirtyFields } = formState;

	const navigate = useNavigate();

	const { title } = watch();

	async function handleUpdateUser() {
		const values = getValues();
		const data = {
			...values,
			user_id: userId
		};
		const response = await saveUser(data);

		if (!response?.error) {
			toast.success('User updated successfully');
			navigate(`/apps/users`);
		} else if (isFetchBaseQueryError(response.error)) {
			toast.error((response.error.data as APIErrorResponse)?.errors?.[0]?.title || 'Something went wrong');
		} else {
			toast.error('Something went wrong');
		}
	}

	async function handleCreateUser() {
		const values = getValues();
		const response = await createUser({ ...values });

		if (!response?.error) {
			toast.success('User created successfully');
			navigate(`/apps/users`);
		} else if (isFetchBaseQueryError(response.error)) {
			toast.error((response.error.data as APIErrorResponse)?.errors?.[0]?.title || 'Something went wrong');
		} else {
			toast.error('Something went wrong');
		}
	}

	async function handleRemoveUser() {
		const response = await deleteUser(userId);

		if (!response?.error) {
			toast.success('User deleted successfully');
			navigate('/apps/users');
		} else if (isFetchBaseQueryError(response.error)) {
			toast.error((response.error.data as APIErrorResponse)?.errors?.[0]?.title || 'Something went wrong');
		} else {
			toast.error('Something went wrong');
		}
	}

	const handleRemove = () => {
		setOpenDeleteDialog(true);
	};

	return (
		<div className="flex flex-col sm:flex-row flex-1 w-full items-center justify-between space-y-2 sm:space-y-0 py-6 sm:py-8">
			<ConfirmationDialog
				open={openDeleteDialog}
				onClose={() => setOpenDeleteDialog(false)}
				onConfirm={handleRemoveUser}
				title="Delete User"
				message="Are you sure you want to delete this user?"
				isLoading={isDeleting}
			/>
			<div className="flex flex-col items-start space-y-2 sm:space-y-0 w-full sm:max-w-full min-w-0">
				<motion.div
					initial={{
						x: 20,
						opacity: 0
					}}
					animate={{
						x: 0,
						opacity: 1,
						transition: { delay: 0.3 }
					}}
				>
					<PageBreadcrumb className="mb-2" />
				</motion.div>

				<div className="flex items-center max-w-full space-x-3">
					<motion.div
						className="flex flex-col min-w-0"
						initial={{ x: -20 }}
						animate={{ x: 0, transition: { delay: 0.3 } }}
					>
						<Typography className="text-lg sm:text-2xl truncate font-semibold">
							{title || 'New User'}
						</Typography>
						<Typography
							variant="caption"
							className="font-medium"
						>
							User Detail
						</Typography>
					</motion.div>
				</div>
			</div>
			<motion.div
				className="flex flex-1 w-full"
				initial={{ opacity: 0, x: 20 }}
				animate={{ opacity: 1, x: 0, transition: { delay: 0.3 } }}
			>
				{userId !== 'new' ? (
					<>
						<Button
							className="whitespace-nowrap mx-1"
							variant="contained"
							color="secondary"
							onClick={handleRemove}
							startIcon={
								isDeleting ? (
									<CircularProgress size={20} />
								) : (
									<FuseSvgIcon className="hidden sm:flex">heroicons-outline:trash</FuseSvgIcon>
								)
							}
						>
							Remove
						</Button>
						<Button
							className="whitespace-nowrap mx-1"
							variant="contained"
							color="secondary"
							disabled={_.isEmpty(dirtyFields) || !isValid || isSaving}
							onClick={handleSubmit(handleUpdateUser)}
							startIcon={isSaving && <CircularProgress size={20} />}
						>
							Save
						</Button>
					</>
				) : (
					<Button
						className="whitespace-nowrap mx-1"
						variant="contained"
						color="secondary"
						disabled={_.isEmpty(dirtyFields) || isAdding}
						onClick={handleSubmit(handleCreateUser)}
						startIcon={isAdding && <CircularProgress size={20} />}
					>
						Add
					</Button>
				)}
			</motion.div>
		</div>
	);
}

export default UserHeader;
